import { atom } from "jotai";
import { atomFamily } from "jotai/utils";
import { nodeAtomFamily } from "./nodeAtomFamily";
import { isDraftNode } from "../utils/isDraftNode";
import { isTemplateNode } from "../utils/isTemplateNode";
import { determineAssetType, getAssetId } from "../utils";
import type { Node } from "@xyflow/react";
import { DefaultNodeData, NodeType } from "../types";

/**
 * Atom family that determines if a node is a draft node but NOT a template node
 * based on its asset properties. Handles the full pipeline of:
 * 1. Getting the node via nodeAtomFamily
 * 2. Extracting asset type and ID
 * 3. Checking draft status (excluding templates)
 *
 * Usage:
 * ```ts
 * const isDraftNotTemplateNode = useAtomValue(isDraftNotTemplateNodeAtomFamily(nodeId));
 * ```
 */
export const isDraftNotTemplateNodeAtomFamily = atomFamily(
  (nodeId: string | undefined | null) => {
    if (!nodeId) {
      return atom(false);
    }

    const baseAtom = atom((get) => {
      const node = get(nodeAtomFamily(nodeId)) as Node | undefined;

      if (!node) {
        return false;
      }

      // Template nodes are excluded (return false for templates)
      if (isTemplateNode(nodeId)) {
        return false;
      }

      // Check if it's a draft node based on asset properties
      const assetType = determineAssetType(
        (node.data as DefaultNodeData)?.services,
        node.type as NodeType,
      );

      const assetId = getAssetId(node.data as any, assetType);

      return isDraftNode({ assetId, assetType });
    });

    baseAtom.debugLabel = `isDraftNotTemplateNodeAtomFamily(${nodeId})`;
    return baseAtom;
  },
);
